import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Plus, Edit2, Trash2, Loader2, Bell } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "../components/RichTextEditor";

export default function AdminNotifications() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [sendEmail, setSendEmail] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await api.get("/notifications");
        setItems(r.data.notifications);
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setEditing(null);
        setTitle(""); setContent(""); setSendEmail(true);
        setOpen(true);
    };
    const openEdit = (n) => {
        setEditing(n);
        setTitle(n.title); setContent(n.content); setSendEmail(false);
        setOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        if (!title.trim()) { toast.error("Title is required"); return; }
        setSubmitting(true);
        try {
            if (editing) {
                await api.put(`/admin/notifications/${editing.id}`, { title, content });
                toast.success("Notification updated");
            } else {
                await api.post("/admin/notifications", { title, content, send_email: sendEmail });
                toast.success(sendEmail ? "Published & emails queued" : "Published");
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save");
        } finally {
            setSubmitting(false);
        }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/admin/notifications/${confirmDelete.id}`);
            toast.success("Deleted");
            setConfirmDelete(null);
            load();
        } catch (err) {
            toast.error("Failed to delete");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-notifications-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Communications</div>
                    <h1 className="font-heading text-3xl font-black tracking-tight mt-1">Notifications</h1>
                    <p className="text-muted-foreground text-sm mt-1">Create rich-text announcements and email all active parents.</p>
                </div>
                <Button onClick={openCreate} className="rounded-full" data-testid="add-notification-btn">
                    <Plus className="h-4 w-4 mr-2" /> New Notification
                </Button>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : items.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-3xl">
                    <Bell className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No notifications yet.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {items.map((n) => (
                        <Card key={n.id} className="rounded-2xl" data-testid={`admin-notification-${n.id}`}>
                            <CardHeader>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <CardTitle className="font-heading">{n.title}</CardTitle>
                                        <CardDescription>{new Date(n.created_at).toLocaleString()} · by {n.created_by}</CardDescription>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        <Button size="icon" variant="ghost" onClick={() => openEdit(n)} data-testid={`edit-notif-${n.id}`}><Edit2 className="h-4 w-4" /></Button>
                                        <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(n)} data-testid={`delete-notif-${n.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="notif-prose text-sm line-clamp-3" dangerouslySetInnerHTML={{ __html: n.content }} />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{editing ? "Edit Notification" : "New Notification"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Title</Label>
                            <Input value={title} onChange={(e) => setTitle(e.target.value)} required className="h-11 rounded-xl" data-testid="notif-title-input" />
                        </div>
                        <div className="space-y-2">
                            <Label>Content</Label>
                            <RichTextEditor value={content} onChange={setContent} />
                        </div>
                        {!editing && (
                            <div className="flex items-center justify-between bg-muted rounded-xl px-4 py-3">
                                <div>
                                    <div className="font-semibold text-sm">Email all active parents</div>
                                    <div className="text-xs text-muted-foreground">A copy of this notification will be emailed.</div>
                                </div>
                                <Switch checked={sendEmail} onCheckedChange={setSendEmail} data-testid="notif-send-email-toggle" />
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" disabled={submitting} className="rounded-full" data-testid="notif-submit-btn">
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Save" : "Publish")}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete notification?</AlertDialogTitle>
                        <AlertDialogDescription>This will remove &quot;{confirmDelete?.title}&quot; permanently.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} className="rounded-full bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
